/**
 * Central balance config (docs/04): formulas live in code, NUMBERS live here.
 * Every value is an integer. Percentages are integer percent (200 = x2.0).
 * The debug panel mutates a working copy at runtime; `defaultConfig` stays frozen.
 */

export type WeaponTag = 'dagger' | 'sword' | 'axe' | 'pistol' | 'rifle' | 'fist'
export type Role = 'tank' | 'assassin' | 'ranged' | 'support' | 'mage'
export type AbilityId =
  | 'bulwark' | 'shadowstrike' | 'piercingShot' | 'healingWave' | 'plagueCloud'
  // Content-Batch 1 (BATTLE-9, Engineering-Draft):
  | 'aegis' | 'fireball' | 'chaosbolt' | 'moonfire' | 'deathHarvest'
  | 'flurry' | 'felNova' | 'chainLightning' | 'boneSpear' | 'bombToss'
export type Rarity = 'common' | 'rare' | 'epic'

export interface WeaponConfig {
  name: string
  hands: number // 1 = Einhaender, 2 = Zweihaender
  base: number // base damage (phys)
  speedPct: number // 100 = x1.0; interval = charBase * speedPct / 100
  range: number
  tag: WeaponTag
  poisonOnHit: number // poison stacks applied per hit (Gift-Dolch)
  rarity: Rarity
}

export interface AffinityConfig {
  /** 'class:two' matches all two-handers, 'tag:<t>' matches a weapon tag */
  match: string
  damagePct: number // bonus damage % with matching weapons
  attackSpeedPct: number // bonus AS % with matching weapons
  rangeBonus: number // bonus range with matching weapons
}

export interface ChampionConfig {
  name: string
  role: Role
  hp: number
  armor: number
  resist: number
  ad: number
  ap: number
  attackBaseTicks: number
  critChancePct: number
  moveTicksPerHex: number
  manaMax: number
  manaStart: number
  ability: AbilityId
  affinity: AffinityConfig | null
  passivePoisonOnHit: number // Gift-Applier-Pfad (Okrazius)
  passiveBurnOnHit: number // Brennen-Applier-Pfad (Magier/Feuer)
  backlineJump: boolean // Assassin-Pfad (Auberon, Pepperion)
  rarity: Rarity
}

/** team-wide passive item (Stage 2, docs/02) — modifies unit stats at combat build time */
export interface PassiveConfig {
  name: string
  rarity: Rarity
  stat: 'hp' | 'ad' | 'ap' | 'armor' | 'resist' | 'attackSpeed'
  pct: number // additive percent bonus, integer
}

export interface BalanceConfig {
  grid: {
    cols: number
    rowsPerSide: number
  }
  combat: {
    tickRate: number
    defenseK: number
    critMultiplierPct: number // 200 = x2.0
    dotIntervalTicks: number
    burnPctMaxHp: number // % MaxHP per DoT interval
    burnIntervals: number
    poisonFlat: number // flat dmg per DoT interval
    poisonIntervals: number
    manaPerAttack: number
    manaPerDamageDivisor: number // +ceil(dmgTaken / divisor) mana when hit
    overtimeStartTick: number
    overtimeMultiplier: number
    hardCutoffTick: number
  }
  power: {
    weightUnitsPct: number // RemainingPower weights (60/40)
    weightHpPct: number
  }
  abilities: {
    bulwark: { radius: number; tauntTicks: number; shield: number }
    shadowstrike: { critBonusPct: number }
    piercingShot: { adPct: number }
    healingWave: { targets: number; baseHeal: number; apPct: number }
    plagueCloud: { radius: number; poisonStacks: number; burnStacks: number }
    // Content-Batch 1 (BATTLE-9):
    aegis: { shieldBase: number; apPct: number; extraTargets: number }
    fireball: { apPct: number; radius: number; burnStacks: number }
    chaosbolt: { apPct: number }
    moonfire: { apPct: number; burnStacks: number }
    deathHarvest: { adPct: number; radius: number; healPerHit: number }
    flurry: { strikes: number; adPct: number }
    felNova: { adPct: number; radius: number; burnStacks: number }
    chainLightning: { apPct: number; secondaryPct: number; jumpRange: number }
    boneSpear: { apPct: number }
    bombToss: { adPct: number; radius: number }
  }
  // ===== Stage 2: Run-Loop / Economy / Shop (docs/01 + 02) =====
  hp: {
    start: number
    stageBaseConst: number // StageBase = const + floor((Round-1)/divisor)
    stageBaseDivisor: number
  }
  economy: {
    baseIncomeStart: number // BaseIncome = start + floor((Round-1)/divisor)
    baseIncomeDivisor: number
    interestPer: number // Zinsen = min(cap, floor(Gold/per))
    interestCap: number
    winBonus: number // +1 Gold bei Clean Win, VOR Zinsen
    lowHpThreshold: number // < 30 HP zu Shop-Beginn
    lowHpFreeRerolls: number
    recoveryRerollTokens: number // bei 2 Losses
    pveLootGold: number // kleiner Loot-Reward wiederkehrender PvE-Runden
    startGold: number
  }
  progression: {
    pveEndRound: number // Runde 1..N = PvE-Start-Phase
    recurringPveStart: number // ab Runde 10 ...
    recurringPveInterval: number // ... alle 5 Runden
    maxRounds: number // Safety-Limit fuer Bots/Tests (0 = kein Limit)
  }
  fracture: {
    repairBase: number // Kosten = base + floor(Round/divisor)
    repairDivisor: number
    repairPerShop: number
    /** Spaltenzonen L/M/R als [vonCol, bisCol] inklusive (0-indexiert) */
    zones: number[][]
  }
  shop: {
    charOfferCount: number
    itemWeaponsDefault: number
    itemPassivesDefault: number
    itemSlotsTotal: number // Waffen+Passives gesamt (Ratio-Shift verschiebt innerhalb)
    rerollSequence: number[] // 1,2,3,5,7,9; danach +rerollStep
    rerollStep: number
    rerollRoundDivisor: number // +floor((Round-1)/10) auf jeden Preis
    crossBonusItemRerolls: number // Character-Pfad -> +2 Free Item-Rerolls
    crossBonusCharRerolls: number // Bonus-Champ abgelehnt -> +1 Free Char-Reroll
    crossBonusDurationRounds: number
    dupCapEnabled: number // S3b: 1 = max dupCapPerBoard Kopien pro Shop-Board (Flag, scharf mit BATTLE-9)
    dupCapPerBoard: number
    bonusChampCommonOnly: number // S1 v0: 1 = Bonus-Charakter zieht nur Commons (5er-Pool)
    tierOfferWeightPct: number // Gewicht hoeherer Waffen-Tiers nach Unlock (vs 100 Basis)
    tierPriceMultPct: number // Preis-Multiplikator pro Waffen-Tier (200 = x2)
  }
  deploy: {
    capCurve: number[] // Runde 1..10
    capFrom11Base: number // ab R11: base + floor((R-11)/2)
    capCeiling: number // Hard Cap 16 ab R31
  }
  bench: { size: number }
  storage: {
    itemStart: number
    itemPerMeta: number
    itemCap: number
  }
  meta: { level: number } // A7: Stub, fix 1
  upgrade: {
    mergeCount: number // 3x gleiche -> naechstes Tier
    weaponMaxTier: number
    charMaxTier: number
    tier2StatMultPct: number // A6: x1.8 auf HP/AD/AP
    tier3StatMultPct: number // nochmal auf Tier-2-Werte
    weaponTierDmgMultPct: number // Waffen-Basis-Schaden pro Tier
  }
  sell: {
    decayPerRounds: number // -1 Gold alle N gehaltene Runden
    minPercent: number // Minimum 50% (aufgerundet)
  }
  rarity: {
    /** Fallback-Odds (nur wenn stageOdds leer) */
    odds: { common: number; rare: number; epic: number }
    /**
     * S3: stage-abhaengige Odds-Matrix (docs/02, loest A9 ab). Zeile gilt ab `fromRound`,
     * die letzte passende Zeile gewinnt. Gewicht 0 = Raritaet AUSGESCHLOSSEN (kein Epsilon-Leck).
     */
    stageOdds: Array<{ fromRound: number; common: number; rare: number; epic: number }>
    /** S3: Seltenheits-Fokus als RELATIVER Multiplikator auf die Stage-Odds (nie negativ) */
    focusRarityMultPct: { common: number; rare: number; epic: number }
    prices: { common: number; rare: number; epic: number }
  }
  focus: {
    ownedWeightMultPct: number // Merge-Fokus: Gewicht besessener Champions x3
    unownedWeightMultPct: number // Vielfalt-Fokus
    goldBonus: number // Gold-Fokus: sofortiger Bonus beim Pick
  }
  ghost: {
    /** Schwierigkeits-Drehregler AUF den regelkonformen Bot-Run (A-CC35): 100 + pct*(stage-1); 0 = fair */
    scalePerRoundPct: number
    pveScalePct: number // PvE-Monster relativ zum PvP-Ghost (100 = gleich)
    /** Einkommens-Annahme des Ghost-Runs: jede N-te Runde zaehlt als Clean Win (0 = nie; A-CC35) */
    assumeCleanWinEvery: number
  }
  passives: Record<string, PassiveConfig>
  champions: Record<string, ChampionConfig>
  weapons: Record<string, WeaponConfig>
}

export const defaultConfig: BalanceConfig = {
  grid: { cols: 7, rowsPerSide: 4 },
  combat: {
    tickRate: 25,
    defenseK: 100,
    critMultiplierPct: 200,
    dotIntervalTicks: 25,
    burnPctMaxHp: 3,
    burnIntervals: 4,
    poisonFlat: 8,
    poisonIntervals: 6,
    manaPerAttack: 10,
    manaPerDamageDivisor: 10,
    overtimeStartTick: 1000,
    overtimeMultiplier: 2,
    hardCutoffTick: 1375,
  },
  power: { weightUnitsPct: 60, weightHpPct: 40 },
  abilities: {
    bulwark: { radius: 2, tauntTicks: 75, shield: 150 },
    shadowstrike: { critBonusPct: 100 },
    piercingShot: { adPct: 150 },
    healingWave: { targets: 2, baseHeal: 120, apPct: 100 },
    plagueCloud: { radius: 2, poisonStacks: 2, burnStacks: 1 },
    aegis: { shieldBase: 90, apPct: 80, extraTargets: 1 },
    fireball: { apPct: 110, radius: 1, burnStacks: 1 },
    chaosbolt: { apPct: 210 },
    moonfire: { apPct: 100, burnStacks: 2 },
    deathHarvest: { adPct: 90, radius: 1, healPerHit: 60 },
    flurry: { strikes: 3, adPct: 70 },
    felNova: { adPct: 80, radius: 1, burnStacks: 1 },
    chainLightning: { apPct: 130, secondaryPct: 60, jumpRange: 2 },
    boneSpear: { apPct: 110 },
    bombToss: { adPct: 110, radius: 1 },
  },
  hp: { start: 100, stageBaseConst: 2, stageBaseDivisor: 5 },
  economy: {
    baseIncomeStart: 5,
    baseIncomeDivisor: 10,
    interestPer: 10,
    interestCap: 4,
    winBonus: 1,
    lowHpThreshold: 30,
    lowHpFreeRerolls: 1,
    recoveryRerollTokens: 1,
    pveLootGold: 2,
    startGold: 0,
  },
  progression: {
    pveEndRound: 3,
    recurringPveStart: 10,
    recurringPveInterval: 5,
    maxRounds: 0,
  },
  fracture: {
    repairBase: 5,
    repairDivisor: 10,
    repairPerShop: 1,
    zones: [[0, 1], [2, 4], [5, 6]],
  },
  shop: {
    charOfferCount: 5,
    itemWeaponsDefault: 2,
    itemPassivesDefault: 3,
    itemSlotsTotal: 5,
    rerollSequence: [1, 2, 3, 5, 7, 9],
    rerollStep: 2,
    rerollRoundDivisor: 10,
    crossBonusItemRerolls: 2,
    crossBonusCharRerolls: 1,
    crossBonusDurationRounds: 2,
    dupCapEnabled: 1, // scharf seit BATTLE-9 (15er-Pool)
    dupCapPerBoard: 2,
    bonusChampCommonOnly: 1,
    tierOfferWeightPct: 25,
    tierPriceMultPct: 200,
  },
  deploy: {
    capCurve: [1, 2, 3, 3, 4, 4, 5, 5, 5, 5],
    capFrom11Base: 6,
    capCeiling: 16,
  },
  bench: { size: 8 },
  storage: { itemStart: 10, itemPerMeta: 5, itemCap: 30 },
  meta: { level: 1 },
  upgrade: {
    mergeCount: 3,
    weaponMaxTier: 3,
    charMaxTier: 3,
    tier2StatMultPct: 180,
    tier3StatMultPct: 180,
    weaponTierDmgMultPct: 150,
  },
  sell: { decayPerRounds: 2, minPercent: 50 },
  rarity: {
    odds: { common: 70, rare: 25, epic: 5 },
    stageOdds: [
      { fromRound: 1, common: 80, rare: 20, epic: 0 },
      { fromRound: 4, common: 78, rare: 20, epic: 2 },
      { fromRound: 7, common: 70, rare: 25, epic: 5 },
      { fromRound: 10, common: 60, rare: 30, epic: 10 },
      { fromRound: 15, common: 50, rare: 34, epic: 16 },
      { fromRound: 20, common: 42, rare: 36, epic: 22 },
    ],
    focusRarityMultPct: { common: 100, rare: 150, epic: 250 },
    prices: { common: 3, rare: 5, epic: 8 },
  },
  focus: {
    ownedWeightMultPct: 300,
    unownedWeightMultPct: 300,
    goldBonus: 5,
  },
  ghost: {
    scalePerRoundPct: 0,
    pveScalePct: 85,
    assumeCleanWinEvery: 2,
  },
  passives: {
    warElixir: { name: 'Wut-Elixier', rarity: 'common', stat: 'ad', pct: 10 },
    focusCrystal: { name: 'Fokus-Kristall', rarity: 'common', stat: 'ap', pct: 10 },
    plating: { name: 'Panzerplatten', rarity: 'common', stat: 'armor', pct: 15 },
    wardSigil: { name: 'Bannzeichen', rarity: 'common', stat: 'resist', pct: 15 },
    vitalityTonic: { name: 'Vitalitaets-Trank', rarity: 'rare', stat: 'hp', pct: 10 },
    accelerator: { name: 'Beschleuniger', rarity: 'rare', stat: 'attackSpeed', pct: 10 },
    warBanner: { name: 'Kriegsbanner', rarity: 'epic', stat: 'ad', pct: 20 },
    archmageSeal: { name: 'Erzmagier-Sigill', rarity: 'epic', stat: 'ap', pct: 20 },
  },
  champions: {
    ironguard: {
      name: 'Sir Kohlrab', // Kohlrabi · Krieger/Tank (docs/12)
      role: 'tank',
      hp: 900, armor: 60, resist: 40, ad: 40, ap: 0,
      attackBaseTicks: 30, critChancePct: 0, moveTicksPerHex: 8,
      manaMax: 80, manaStart: 0,
      ability: 'bulwark',
      affinity: { match: 'class:two', damagePct: 10, attackSpeedPct: 0, rangeBonus: 0 },
      passivePoisonOnHit: 0,
      passiveBurnOnHit: 0,
      backlineJump: false,
      rarity: 'common',
    },
    rogue: {
      name: 'Auberon', // Aubergine · Schurke/Assassin (docs/12)
      role: 'assassin',
      hp: 550, armor: 25, resist: 25, ad: 70, ap: 0,
      attackBaseTicks: 18, critChancePct: 25, moveTicksPerHex: 4,
      manaMax: 60, manaStart: 0,
      ability: 'shadowstrike',
      affinity: { match: 'tag:dagger', damagePct: 0, attackSpeedPct: 15, rangeBonus: 0 },
      passivePoisonOnHit: 0,
      passiveBurnOnHit: 0,
      backlineJump: true,
      rarity: 'rare',
    },
    musketeer: {
      name: 'Möhrax', // Karotte/Möhre · Jäger/Ranged (docs/12)
      role: 'ranged',
      hp: 500, armor: 20, resist: 20, ad: 65, ap: 0,
      attackBaseTicks: 22, critChancePct: 15, moveTicksPerHex: 6,
      manaMax: 70, manaStart: 0,
      ability: 'piercingShot',
      affinity: { match: 'tag:rifle', damagePct: 0, attackSpeedPct: 0, rangeBonus: 1 },
      passivePoisonOnHit: 0,
      passiveBurnOnHit: 0,
      backlineJump: false,
      rarity: 'common',
    },
    cleric: {
      name: 'Bruder Knobi', // Knoblauch · Priester/Support (docs/12)
      role: 'support',
      hp: 600, armor: 30, resist: 50, ad: 25, ap: 60,
      attackBaseTicks: 28, critChancePct: 0, moveTicksPerHex: 6,
      manaMax: 90, manaStart: 0,
      ability: 'healingWave',
      affinity: null, // Staff affinity (+15% healing) — no staff in v0 weapon list
      passivePoisonOnHit: 0,
      passiveBurnOnHit: 0,
      backlineJump: false,
      rarity: 'rare',
    },
    hexmaster: {
      name: 'Okrazius', // Okra · Hexendoktor/Magie-DPS (docs/12)
      role: 'mage',
      hp: 520, armor: 18, resist: 35, ad: 20, ap: 75,
      attackBaseTicks: 26, critChancePct: 10, moveTicksPerHex: 6,
      manaMax: 75, manaStart: 0,
      ability: 'plagueCloud',
      affinity: null, // Focus affinity (+20% DoT) — no focus item in v0 weapon list
      passivePoisonOnHit: 1,
      passiveBurnOnHit: 0,
      backlineJump: false,
      rarity: 'epic',
    },
    // ===== Content-Batch 1 (BATTLE-9, Engineering-Draft A-CC33 — Nyra-Feinschliff folgt) =====
    paladin: {
      name: 'Ananias', // Ananas · Paladin/Schutz — Tank-Support, Schild-Segen
      role: 'tank',
      hp: 820, armor: 50, resist: 50, ad: 35, ap: 30,
      attackBaseTicks: 30, critChancePct: 0, moveTicksPerHex: 8,
      manaMax: 90, manaStart: 0,
      ability: 'aegis',
      affinity: { match: 'tag:sword', damagePct: 10, attackSpeedPct: 0, rangeBonus: 0 },
      passivePoisonOnHit: 0, passiveBurnOnHit: 0, backlineJump: false,
      rarity: 'common',
    },
    mage: {
      name: 'Beetrix', // Rote Beete · Magier/Feuer — Brennen-Applier, crit-lastig
      role: 'mage',
      hp: 480, armor: 15, resist: 30, ad: 15, ap: 85,
      attackBaseTicks: 26, critChancePct: 20, moveTicksPerHex: 6,
      manaMax: 70, manaStart: 0,
      ability: 'fireball',
      affinity: null, // Stab-Affinitaet folgt mit Stab-Waffen (A-CC10-Muster)
      passivePoisonOnHit: 0, passiveBurnOnHit: 1, backlineJump: false,
      rarity: 'rare',
    },
    warlock: {
      name: 'Brombeus', // Brombeere · Hexenmeister/Zerstoerung — Einzelziel-Burst
      role: 'mage',
      hp: 500, armor: 15, resist: 40, ad: 15, ap: 90,
      attackBaseTicks: 28, critChancePct: 15, moveTicksPerHex: 6,
      manaMax: 85, manaStart: 0,
      ability: 'chaosbolt',
      affinity: null,
      passivePoisonOnHit: 0, passiveBurnOnHit: 0, backlineJump: false,
      rarity: 'epic',
    },
    druid: {
      name: 'Kürbius', // Kürbis · Druide/Gleichgewicht — zaeher Naturmagier, Mondfeuer
      role: 'mage',
      hp: 650, armor: 30, resist: 40, ad: 30, ap: 55,
      attackBaseTicks: 27, critChancePct: 0, moveTicksPerHex: 6,
      manaMax: 80, manaStart: 0,
      ability: 'moonfire',
      affinity: null,
      passivePoisonOnHit: 0, passiveBurnOnHit: 0, backlineJump: false,
      rarity: 'common',
    },
    deathknight: {
      name: 'Mortinak', // Pastinake · Todesritter/Blut — Bruiser, erntet Leben
      role: 'tank',
      hp: 750, armor: 45, resist: 35, ad: 55, ap: 0,
      attackBaseTicks: 28, critChancePct: 5, moveTicksPerHex: 7,
      manaMax: 85, manaStart: 0,
      ability: 'deathHarvest',
      affinity: { match: 'class:two', damagePct: 10, attackSpeedPct: 0, rangeBonus: 0 },
      passivePoisonOnHit: 0, passiveBurnOnHit: 0, backlineJump: false,
      rarity: 'rare',
    },
    monk: {
      name: 'Birnhard', // Birne · Moench/Windlaeufer — Faust-Wirbel, hohe Angriffsgeschwindigkeit
      role: 'assassin',
      hp: 600, armor: 30, resist: 30, ad: 45, ap: 0,
      attackBaseTicks: 14, critChancePct: 10, moveTicksPerHex: 5,
      manaMax: 65, manaStart: 0,
      ability: 'flurry',
      affinity: { match: 'tag:fist', damagePct: 0, attackSpeedPct: 25, rangeBonus: 0 },
      passivePoisonOnHit: 0, passiveBurnOnHit: 0, backlineJump: false,
      rarity: 'common',
    },
    demonhunter: {
      name: 'Pepperion', // Peperoni · Daemonenjaeger/Verwuestung — Backline-Springer mit Fel-Nova
      role: 'assassin',
      hp: 520, armor: 22, resist: 30, ad: 65, ap: 20,
      attackBaseTicks: 17, critChancePct: 20, moveTicksPerHex: 4,
      manaMax: 70, manaStart: 0,
      ability: 'felNova',
      affinity: { match: 'tag:dagger', damagePct: 0, attackSpeedPct: 15, rangeBonus: 0 },
      passivePoisonOnHit: 0, passiveBurnOnHit: 0, backlineJump: true,
      rarity: 'epic',
    },
    shaman: {
      name: 'Kokolo', // Kokosnuss · Schamane/Elementar — Ketten-Blitz
      role: 'mage',
      hp: 560, armor: 25, resist: 45, ad: 20, ap: 70,
      attackBaseTicks: 27, critChancePct: 0, moveTicksPerHex: 6,
      manaMax: 75, manaStart: 0,
      ability: 'chainLightning',
      affinity: null,
      passivePoisonOnHit: 0, passiveBurnOnHit: 0, backlineJump: false,
      rarity: 'rare',
    },
    necromancer: {
      name: 'Nekrettich', // Schwarzer Rettich · Nekromant/Knochen — Knochenspeer durch die Reihe
      role: 'mage',
      hp: 490, armor: 18, resist: 38, ad: 15, ap: 88,
      attackBaseTicks: 27, critChancePct: 10, moveTicksPerHex: 6,
      manaMax: 80, manaStart: 0,
      ability: 'boneSpear',
      affinity: null,
      passivePoisonOnHit: 0, passiveBurnOnHit: 0, backlineJump: false,
      rarity: 'epic',
    },
    tinker: {
      name: 'Doc Walnuss', // Walnuss · Tueftler/Sprengmeister — Bomben-AoE, Tech
      role: 'ranged',
      hp: 540, armor: 28, resist: 25, ad: 55, ap: 25,
      attackBaseTicks: 24, critChancePct: 10, moveTicksPerHex: 6,
      manaMax: 75, manaStart: 0,
      ability: 'bombToss',
      affinity: { match: 'tag:pistol', damagePct: 0, attackSpeedPct: 0, rangeBonus: 1 },
      passivePoisonOnHit: 0, passiveBurnOnHit: 0, backlineJump: false,
      rarity: 'rare',
    },
  },
  weapons: {
    fist: { name: 'Faust', hands: 1, base: 10, speedPct: 100, range: 1, tag: 'fist', poisonOnHit: 0, rarity: 'common' },
    dagger: { name: 'Dolch', hands: 1, base: 25, speedPct: 70, range: 1, tag: 'dagger', poisonOnHit: 0, rarity: 'common' },
    poisondagger: { name: 'Gift-Dolch', hands: 1, base: 20, speedPct: 70, range: 1, tag: 'dagger', poisonOnHit: 1, rarity: 'rare' },
    shortsword: { name: 'Kurzschwert', hands: 1, base: 35, speedPct: 100, range: 1, tag: 'sword', poisonOnHit: 0, rarity: 'common' },
    greataxe: { name: 'Grossaxt', hands: 2, base: 80, speedPct: 140, range: 1, tag: 'axe', poisonOnHit: 0, rarity: 'rare' },
    pistol: { name: 'Pistole', hands: 1, base: 30, speedPct: 100, range: 2, tag: 'pistol', poisonOnHit: 0, rarity: 'common' },
    rifle: { name: 'Gewehr', hands: 2, base: 55, speedPct: 120, range: 4, tag: 'rifle', poisonOnHit: 0, rarity: 'epic' },
  },
}

/** Deep clone (config is plain JSON data) */
export function cloneConfig(c: BalanceConfig): BalanceConfig {
  return JSON.parse(JSON.stringify(c)) as BalanceConfig
}

/** Default weapon loadout per champion — NUR Sandbox-Editor + Sim-Presets/Tests (Ghost-Seats nutzen seit A-CC35 echte Shop-Kaeufe) */
export const defaultLoadout: Record<string, string[]> = {
  ironguard: ['greataxe'],
  rogue: ['dagger', 'dagger'],
  musketeer: ['rifle'],
  cleric: ['shortsword'],
  hexmaster: ['fist'],
  // Content-Batch 1 (BATTLE-9)
  paladin: ['shortsword'],
  mage: ['fist'],
  warlock: ['fist'],
  druid: ['fist'],
  deathknight: ['greataxe'],
  monk: [], // Faeuste — Affinitaet tag:fist (+25% AS)
  demonhunter: ['dagger', 'dagger'],
  shaman: ['fist'],
  necromancer: ['fist'],
  tinker: ['pistol'],
}
